import { compact } from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'

import { logger } from '~/debug/logger'
import { useCurrentOrgId } from '~/store/slices/currentOrg'

import type { UserSummaryModel } from '../client/fleet-management'
import { useGetUsersQuery } from '../client/fleet-management'
import { combineSkip } from '../hooks/utils'
import type { IndexedDbService } from './storage/IndexedDbService'
import type { OrgTreeUserCacheRecord } from './storage/schema'

// The endpoint is uncapped, so a single large page returns every user in the
// org tree in one request (no client-side pagination needed for the picker).
const ORG_TREE_USERS_PAGE_SIZE = 5000
// Include the full descendant subtree of the requesting user's org.
const ORG_TREE_USERS_DEPTH = 100

export type OrgTreeUsersDataManagerContextValue = {
  data: UserSummaryModel[]
  isLoading: boolean
  isFetching: boolean
}

function useOrgTreeUsersQuery(options?: { skip?: boolean }) {
  // The endpoint resolves its org tree from the requesting user's own organization, so the response
  // covers the principal org tree and is identical however the caller is cloaked. `useManagedOrgTreeUsers`
  // narrows it to the effective org; the org id here only gates the request until one is known.
  const currentOrgId = useCurrentOrgId()

  const { data, isLoading, isFetching } = useGetUsersQuery(
    { page: 0, count: ORG_TREE_USERS_PAGE_SIZE, depth: ORG_TREE_USERS_DEPTH },
    combineSkip(!currentOrgId, options)
  )

  return {
    orgTreeUsers: data?.data,
    isLoading,
    isFetching
  }
}

export default function useOrgTreeUsersDataManager(service: IndexedDbService) {
  const { orgTreeUsers, isLoading, isFetching } = useOrgTreeUsersQuery()

  const inMemoryUsers = useOrgTreeUsersPersistence(service, orgTreeUsers)

  return useMemo<OrgTreeUsersDataManagerContextValue>(
    () => ({
      data: inMemoryUsers,
      isLoading,
      isFetching
    }),
    [inMemoryUsers, isLoading, isFetching]
  )
}

function useOrgTreeUsersPersistence(service: IndexedDbService, orgTreeUsers?: UserSummaryModel[]) {
  const [inMemoryUsers, setInMemoryUsers] = useState<UserSummaryModel[]>([])
  const receivedApiDataRef = useRef(false)

  useEffect(() => {
    async function hydrateOrgTreeUsersCache() {
      try {
        const records = await service.getAll('orgTreeUsers')
        if (!receivedApiDataRef.current) {
          setInMemoryUsers(records.map((record) => record.payload))
        }
      } catch (error) {
        logger.error('Failed to hydrate org-tree users cache', error)
      }
    }

    void hydrateOrgTreeUsersCache()
  }, [service])

  useEffect(() => {
    if (!orgTreeUsers) return

    receivedApiDataRef.current = true
    setInMemoryUsers(orgTreeUsers)

    const fetchedAt = Date.now()
    // Drop id-less users: the store is keyed by id, and an unidentified user
    // can't be selected or resolved as a recipient anyway.
    const records = compact(
      orgTreeUsers.map((user): OrgTreeUserCacheRecord | undefined =>
        user.id ? { id: user.id, payload: user, syncedAt: fetchedAt } : undefined
      )
    )

    const replaceRecords = async () => {
      try {
        await service.replace('orgTreeUsers', records)
      } catch (error) {
        logger.error('Failed to persist org-tree users snapshot', error)
      }
    }

    void replaceRecords()
  }, [orgTreeUsers, service])

  return inMemoryUsers
}
